// FLAGS: silent-failure
// Configures i18next for the five locales and, because URL segments are themselves translation keys, owns
// the route-path lookup the whole app navigates through. When no input names a language the app falls back to
// `FALLBACK_LANGUAGE_CODE`, Dutch, while the shared components fall back to English; the boot reports the miss.
// Hostname-as-language itself lives in `@src/utils/hostLanguage`; this module only consumes it.
// Behind the LanguagePathPrefix flag two inputs come before the hostname: the URL path prefix
// (`@src/utils/languagePathPrefix`) and the signed-in user's profile language, which the boot reads from the
// stored user and passes in — this module never reads the user record. The first code i18next can
// serve wins; `supportedLngs` makes it skip a code it cannot serve instead of applying it, so an unknown prefix
// or profile code falls through to the next input.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import sessionStorageService from "@src/services/sessionStorage.service";

import sharedTranslationsDe from "@openup/shared/i18n/de.json";
import sharedTranslationsEn from "@openup/shared/i18n/en.json";
import sharedTranslationsFr from "@openup/shared/i18n/fr.json";
import sharedTranslationsNl from "@openup/shared/i18n/nl.json";
import sharedTranslationsEs from "@openup/shared/i18n/es.json";

import translationsNl from "@src/locales/nl.json";
import translationsEn from "@src/locales/en.json";
import translationsFr from "@src/locales/fr.json";
import translationsDe from "@src/locales/de.json";
import translationsEs from "@src/locales/es.json";

import { getLanguageForHost, isLanguageHost } from "@src/utils/hostLanguage";
import { getLanguageForPath } from "@src/utils/languagePathPrefix";

type Translations = Record<string, string>;

// A full tag like every other code in the system, so the fallback compares equal to a Dutch hostname or profile
// answer instead of sitting beside them as a bare `nl`. Resources stay keyed by bare code; i18next resolves
// `nl-NL` down to them.
const FALLBACK_LANGUAGE_CODE = "nl-NL";

// Bare codes on purpose: these key the resource and route tables, and `supportedLngs` compares against them
// after `nonExplicitSupportedLngs` has stripped the region off a candidate, so `de-DE` is accepted because `de`
// is here. Codes that get *applied*, the fallback above and every detector answer, are full tags.
const languages = ["nl", "en", "de", "fr", "es"];

const sharedTranslations: Record<string, Translations> = {
  nl: sharedTranslationsNl,
  en: sharedTranslationsEn,
  fr: sharedTranslationsFr,
  de: sharedTranslationsDe,
  es: sharedTranslationsEs,
};

const localTranslations: Record<string, Translations> = {
  nl: translationsNl,
  en: translationsEn,
  fr: translationsFr,
  de: translationsDe,
  es: translationsEs,
};

const combinedTranslations = languages.reduce<Record<string, Translations>>(
  (acc, lang) => {
    acc[lang] = { ...sharedTranslations[lang], ...localTranslations[lang] };
    return acc;
  },
  {},
);

const generateRouteTranslations = (
  lang: string,
): { translation: Translations } => ({
  translation: Object.keys(combinedTranslations[lang])
    .filter((key) => key.startsWith("route"))
    .reduce<Translations>((obj, key) => {
      obj[key] = combinedTranslations[lang][key];
      return obj;
    }, {}),
});

const routeTranslations = languages.reduce<
  Record<string, { translation: Translations }>
>((acc, lang) => {
  acc[lang] = generateRouteTranslations(lang);
  return acc;
}, {});

const isDebug = () => import.meta.env.MODE === "development";

const domainLanguageDetector = "domainLanguageDetector";
const pathPrefixLanguageDetector = "pathPrefixLanguageDetector";
const profileLanguageDetector = "profileLanguageDetector";
const languageDetector = new LanguageDetector();

// The language the URL path prefix names, if any: `/de/…` → `de-DE`.
export const getPathLanguage = (): string | undefined =>
  getLanguageForPath(window.location.pathname);

// The language inputs the boot supplies: whether the flag is on, and the profile language it read from the stored
// user. The same object feeds `collectLanguageCandidates` and `initI18n`, so both see one read.
export interface LanguageInputs {
  languagePathPrefix?: boolean;
  profileLanguage?: string;
}

// The profile language `initI18n` was given, which is what the profile detector answers. Set once, at init.
let initProfileLanguage: string | undefined;

// The hostname's language on a deployed language host; off one (localhost, a preview host) the session cache this
// same detector writes. `undefined` when neither answers.
export const getHostLanguage = (): string | undefined =>
  isLanguageHost() && !isDebug()
    ? getLanguageForHost()
    : (sessionStorage.getItem("language") ?? undefined);

// The detectors i18next consults, in order; the first code it can serve wins. Both new inputs exist only behind
// the flag, so flag-off keeps today's single hostname detector.
export const getLanguageDetectionOrder = (
  languagePathPrefix: boolean,
): string[] =>
  languagePathPrefix
    ? [
        pathPrefixLanguageDetector,
        profileLanguageDetector,
        domainLanguageDetector,
      ]
    : [domainLanguageDetector];

// Whether i18next can serve `code` under the `supportedLngs` set at init (`de-DE` counts as `de`). False before
// init, when there is nothing to check against.
export const isSupportedLanguage = (code: string): boolean => {
  const languageUtils = i18n.services?.languageUtils as
    | { isSupportedCode?: (candidate: string) => boolean }
    | undefined;
  return languageUtils?.isSupportedCode?.(code) ?? false;
};

export type LanguageSource = "prefix" | "profile" | "hostname";
export type LanguageMiss = "no-input" | "unsupported";

export interface LanguageCandidate {
  source: LanguageSource;
  code: string;
}

export interface LanguageResolution {
  // Every input's answer, in detection order — the list i18next chooses from.
  candidates: LanguageCandidate[];
  // The first answer, which is what the resolver chose; absent when no input answered.
  resolved?: LanguageCandidate;
  // What i18next applied after `supportedLngs` filtering and the fallback.
  applied: string;
  // `no-input`: nothing answered, so the fallback rendered. `unsupported`: the chosen code is one i18next cannot
  // serve, so it silently skipped it. Absent when the resolution was clean.
  miss?: LanguageMiss;
}

const LANGUAGE_LOOKUPS: Record<
  string,
  {
    source: LanguageSource;
    lookup: (inputs: LanguageInputs) => string | undefined;
  }
> = {
  [pathPrefixLanguageDetector]: {
    source: "prefix",
    lookup: () => getPathLanguage(),
  },
  [profileLanguageDetector]: {
    source: "profile",
    lookup: ({ profileLanguage }) => profileLanguage,
  },
  [domainLanguageDetector]: {
    source: "hostname",
    lookup: () => getHostLanguage(),
  },
};

// Every input's answer, in detection order — the same answers the detectors give. Call this BEFORE `initI18n()`:
// off a language host, i18next writes whatever it settled on into the session cache during init, so a replay
// afterwards would find an answer no detector gave and a miss would never be seen.
export const collectLanguageCandidates = (
  inputs: LanguageInputs,
): LanguageCandidate[] =>
  getLanguageDetectionOrder(inputs.languagePathPrefix ?? false).flatMap(
    (name) => {
      const { source, lookup } = LANGUAGE_LOOKUPS[name];
      const code = lookup(inputs);
      return code ? [{ source, code }] : [];
    },
  );

// Says what the resolver chose and whether i18next could honour it. Needs init to have run (for `applied` and
// the supported set), which is why the candidates are collected separately, before it, and passed in — no
// default, because collecting here would read the cache init has just written. This is the only place a
// resolution miss becomes visible: i18next falls back rather than throwing.
export const describeLanguageResolution = (
  candidates: LanguageCandidate[],
): LanguageResolution => {
  const [resolved] = candidates;
  let miss: LanguageMiss | undefined;
  if (resolved === undefined) {
    miss = "no-input";
  } else if (!isSupportedLanguage(resolved.code)) {
    miss = "unsupported";
  }
  return { candidates, resolved, applied: i18n.language, miss };
};

const getBestMatchingPartialRouteForLocale = (
  route: string,
  locale: string,
): { route: string; remainder: string } => {
  const routes = routeTranslations[locale].translation;
  const paths = [...new Set(Object.values(routes))].map(
    (path) => `/${path.toLowerCase()}`,
  );

  let longestMatchRoute: string | undefined = "";
  let remainder = "";

  if (route) {
    const lowerCaseRoute = route.toLowerCase();
    const longestMatchPath = paths
      .filter(
        (path) =>
          path.length > 1 &&
          // The match has to end where a segment ends. A bare `startsWith` lets a route value swallow the
          // front of a longer word — `route.onboarding.queryParam.employer` is the single letter `o`, so
          // `/onbekend` would decode as that key plus a stray `nbekend`, and `/overzichtelijk` as `overzicht`
          // plus `elijk`. Anything built back from those parts is a path that never existed.
          (lowerCaseRoute === path || lowerCaseRoute.startsWith(`${path}/`)),
      )
      .reduce(
        (longest, path) => (path.length > longest.length ? path : longest),
        "",
      );

    if (longestMatchPath) {
      longestMatchRoute = Object.keys(routes).find(
        (k) => `/${routes[k].toLowerCase()}` === longestMatchPath,
      );
      remainder = route.substring(longestMatchPath.length);
    }
  }
  if (longestMatchRoute) {
    return {
      route: longestMatchRoute,
      remainder,
    };
  }
  return {
    route,
    remainder,
  };
};

const trimRoute = (route: string): string => {
  if (route?.startsWith("/")) {
    return route.slice(1);
  }
  return route;
};

export const getBestMatchingRouteForLocale = (
  route: string | undefined,
  locale: string,
): string[] => {
  const parts: string[] = [];
  let routeReducer = route;
  while (routeReducer) {
    const result = getBestMatchingPartialRouteForLocale(routeReducer, locale);
    result.route = trimRoute(result.route);
    parts.push(result.route);
    routeReducer = result.remainder;
  }

  return parts;
};

// A route key's place in one language's table, and the keys that share a value there. Two keys can share a
// value — `bevestiging` is both the booking and the cancellation confirmation in Dutch — which is what makes
// the reverse lookup lossy. Compared lower-cased, the way the matcher compares. The key sets are built once per
// language: a `Set` rather than an `in` check, so a raw segment spelled like an object prototype name is never a
// route key.
const routeKeySets: Record<string, Set<string>> = {};

const isRouteKey = (locale: string, part: string): boolean => {
  routeKeySets[locale] ??= new Set(
    Object.keys(routeTranslations[locale].translation),
  );
  return routeKeySets[locale].has(part);
};

const routeKeysSharingValue = (locale: string, key: string): string[] => {
  const routes = routeTranslations[locale].translation;
  const wanted = routes[key].toLowerCase();
  return Object.keys(routes).filter(
    (candidate) => routes[candidate].toLowerCase() === wanted,
  );
};

// `/overzicht` in Dutch is `/uebersicht` in German: every route segment is a translation key, so a path only
// matches the route table built for the language it was written in. This is the same key round-trip the
// cross-locale handoff does (`views/auth/LocalizationRedirectView.jsx:44`), performed in place instead of across
// a domain hop. The path comes back unchanged whenever translating it could do harm: when it names no route,
// when it is already written in the language arriving, and when a segment decodes ambiguously (below).
export const translatePathToLocale = (
  pathname: string,
  fromLanguage: string,
  toLanguage: string,
): string => {
  // `supportedLngs` only filters what the detectors may apply; an explicit `changeLanguage` can still leave
  // `i18n.language` set to a code this app ships no route table for. Reading one would throw, and both callers
  // run where a throw costs the whole page, so an unknown language means "leave the path alone".
  const from = fromLanguage.substring(0, 2);
  const to = toLanguage.substring(0, 2);
  if (
    routeTranslations[from] === undefined ||
    routeTranslations[to] === undefined
  ) {
    return pathname;
  }
  // Already written in the language arriving — a reload after an earlier rewrite, or a value both languages
  // share — so there is nothing to translate. Decided on the first part, because a deep link is a route
  // followed by a slug that decodes to nothing in any language. A value both languages use for different
  // routes cannot be told apart from the path alone; keeping the URL means the router shows what it names in
  // the language arriving, a real page, where a rewrite on every reload turned it into a missing one.
  const [leading] = getBestMatchingRouteForLocale(pathname, to);
  if (leading !== undefined && isRouteKey(to, leading)) {
    return pathname;
  }
  const keys = getBestMatchingRouteForLocale(pathname, from);
  if (keys.length === 0) {
    return pathname;
  }
  const translate = i18n.getFixedT(toLanguage);
  // A value several keys share decodes to whichever key the locale file lists first. When those keys translate
  // to different values, the guess sends the visitor to the wrong page — a booking confirmation for a
  // cancellation — with the URL already replaced. Left alone, the path misses the new route table and the
  // not-found card shows over the URL the visitor actually had, which they can at least go back from.
  const ambiguous = keys.some((key) => {
    if (!isRouteKey(from, key)) {
      return false;
    }
    const targets = new Set(
      routeKeysSharingValue(from, key).map((candidate) => translate(candidate)),
    );
    return targets.size > 1;
  });
  if (ambiguous) {
    return pathname;
  }
  return `/${keys.map((key) => translate(key)).join("/")}`;
};

languageDetector.addDetector({
  name: domainLanguageDetector,
  lookup: () => getHostLanguage(),
  cacheUserLanguage(lng: string) {
    // Off a language host the tab remembers the choice — except one the URL prefix made. That is URL state, and
    // caching it would make every bare URL opened later in the tab inherit it.
    if (!isLanguageHost() && lng !== getPathLanguage()) {
      sessionStorage.setItem("language", lng);
    }
  },
});

languageDetector.addDetector({
  name: pathPrefixLanguageDetector,
  lookup: () => getPathLanguage(),
});

languageDetector.addDetector({
  name: profileLanguageDetector,
  lookup: () => initProfileLanguage,
});

const combinedTranslationsForResources = Object.keys(
  combinedTranslations,
).reduce<Record<string, { translation: Translations }>>((acc, lang) => {
  acc[lang] = { translation: combinedTranslations[lang] };
  return acc;
}, {});

// Init is deferred to the bootstrap (`src/boot/bootstrap.ts`) so it runs after the LanguagePathPrefix flag is known
// and can be passed in here, with the profile language the boot read. With the flag off the detector list is
// today's single hostname detector and the profile language goes unread. One thing the
// flag does not gate is `cacheUserLanguage`'s prefix check above, which reads the path either way — off a language
// host, a URL starting with a bare locale code now skips the cache write it used to make. No route starts with one,
// so nothing reaches it today. Idempotent: a second call, whatever its options, changes nothing.
export const initI18n = ({
  languagePathPrefix = false,
  profileLanguage,
}: LanguageInputs = {}) => {
  if (i18n.isInitialized) {
    return i18n;
  }
  initProfileLanguage = profileLanguage;
  i18n
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      resources: sessionStorageService.dontLoadTranslations()
        ? routeTranslations
        : combinedTranslationsForResources,
      fallbackLng: FALLBACK_LANGUAGE_CODE,
      // The codes i18next may apply; `de-DE` counts as `de`. Anything else is skipped at detection time and
      // falls through to the next detector, so a bad prefix or profile code cannot become the page language.
      supportedLngs: languages,
      nonExplicitSupportedLngs: true,
      detection: {
        order: getLanguageDetectionOrder(languagePathPrefix),
        caches: [domainLanguageDetector],
      },
      keySeparator: false,
      interpolation: {
        skipOnVariables: false,
        escapeValue: false, // react already safes from xss
      },
    });
  return i18n;
};

export default i18n;
